import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import { CookiesProvider } from 'react-cookie'
import { store } from './store/store'
import reportWebVitals from './reportWebVitals'
import './index.css'
import { App } from './App'
import { IntercomProvider } from 'react-use-intercom'
import * as Sentry from '@sentry/react'
import { HotjarBootstrap } from './components/Hotjar/HotjarBootstrap'

Sentry.init({
  dsn: 'https://18fa83fd281d5bdac8f04cf622c239d7@o4507073859682304.ingest.us.sentry.io/4509995677843457',
  enabled: import.meta.env.REACT_APP_SENTRY_ENABLED === 'true',
  environment: import.meta.env.REACT_APP_ENV,
  /**
   * This release structure should follow app-name@version and must match the --release flag value in sentry:sourcemaps:upload scripts
   * Find more info here - https://docs.sentry.io/platforms/javascript/configuration/releases/#setting-a-release
   */
  release: `professional-dashboard@${import.meta.env.REACT_APP_VERSION}`,
  /**
   * Maximum number of chars a single value can have before it will be truncated.
   */
  maxValueLength: 1000,
  /**
   * Maximum number of levels that normalization algorithm will traverse in objects and arrays.
   * Used when normalizing an event before sending, on all of the listed attributes:
   * - `breadcrumbs.data`
   * - `user`
   * - `contexts`
   * - `extra`
   */
  normalizeDepth: 10,
  integrations: [
    Sentry.extraErrorDataIntegration({ depth: 10 }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  /**
   * This will ensure that Sentry only enables trace propagation for API calls made to localhost
   * and any of zocks.io subdomains, preventing unnecessary noise from third-party services.
   */
  tracePropagationTargets: ['localhost', /^https:\/\/(.*\.)?zocks\.io/],
})

const container = document.getElementById('root')!
const root = createRoot(container)

root.render(
  <Provider store={store}>
    <IntercomProvider appId={import.meta.env.REACT_APP_INTERCOM_APPID!}>
      <CookiesProvider>
        <HotjarBootstrap />
        <App />
      </CookiesProvider>
    </IntercomProvider>
  </Provider>,
)

// If you want to start measuring performance in your store, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
